import { toast } from "react-toastify";
import api from "../../services/api";
import newApi from "../../services/newApi/api";
import type {
  ICreateAdditionRequestUtilsParams,
  ICreateRequestCompulsoryParams,
  ICreateRequestCompulsoryResponse,
  IGetAvailableExamsParams,
  IGetAvailableExamsResponse,
  IGetRequestDetailResponse,
  IHandleSubmitParams,
  IRequestAdditionFilterParams,
} from "./types";
import { REQUEST_ADDITION_STATUS_CONTENT } from "../../constants/requestAddition";
import type { RequestCounters } from "../../hooks/useRequestCounters";

export function createAdditionRequestUtils(
  params: ICreateAdditionRequestUtilsParams,
) {
  const {
    setAvailableExams,
    setRequestsMadeForPatient: _setRequestsMadeForPatient,
    selectedExams,
    setNewRequestId,
    setIsLoading,
    setFiles,
    setSelectedExams,
  } = params;

  async function uploadFiles(file: File) {
    const formData = new FormData();

    formData.append("file", file);

    const upload = (await api.post("upload", formData)).data;
    return { ...file, isUploaded: true, path: upload.url };
  }

  return {
    async getRequests(
      requestAdditionFilterParams: IRequestAdditionFilterParams,
    ) {
      try {
        const filteredItems = await newApi.get(
          "/addition/filter",
          { params: requestAdditionFilterParams },
        );

        return filteredItems.data.data;
      } catch (error: any) {
        toast.error(error?.response?.data?.message);
      }
    },

    async getReasons(): Promise<{ id: number; reason: string }[] | undefined> {
      const reasonsData = (await newApi.get("/addition/reasons")).data.data
        .reasons;
      return reasonsData;
    },

    async getAvailableExams(params: IGetAvailableExamsParams) {
      try {
        const response = await newApi.get<IGetAvailableExamsResponse>(
          `/addition/available-exams/${params.uuid}/${params.atendimento}`,
          {
            params: {
              perPage: params.perPage,
              page: params.page,
              name: params?.name ?? "",
            },
          },
        );
        if (selectedExams?.exams.length === 0) {
          setAvailableExams(response.data);
          return response.data;
        }
        const mapped = response.data.exams.map((exam: any) => {
          return {
            ...exam,
            selected:
              selectedExams?.exams.some((item: any) => item.code === exam.code) ??
              false,
          };
        });
        setAvailableExams({
          ...response.data,
          exams: mapped,
          total: response.data.total - (selectedExams?.total ?? 0),
        });
        return {
          ...response.data,
          exams: mapped,
          total: response.data.total - (selectedExams?.total ?? 0),
        };
      } catch (error: any) {
        toast.error(error?.response?.data?.message);
      } finally {
      }
    },

    async handleSubmit({
      files,
      appointmentId,
      exams,
      requestAdditionId,
    }: IHandleSubmitParams) {
      try {
        setIsLoading(true);

        const examCodes =
          exams && exams.length > 0
            ? exams.map((exam) => Number(exam.code))
            : null;

        const uploadedFiles = [...files];

        await Promise.all(
          uploadedFiles.map(async (item, index) => {
            if ((item as any).isUploaded) return;

            const uploadSuccess = await uploadFiles(item);
            if (uploadSuccess) {
              uploadedFiles[index] = uploadSuccess;
            }
          }),
        );

        const mappedFiles = uploadedFiles.map((file: any) => {
          if (!file.isUploaded) {
            toast.error(
              "Há arquivos com erro, ajuste-os ou remova-os antes de confirmar",
            );
            throw Error();
          }
          return file.path;
        });

        if (requestAdditionId) {
          const response = await newApi.put<{ data: any }>(
            `/addition/${requestAdditionId}`,
            {
              appointment_id: appointmentId,
              files: mappedFiles,
              exam_code: examCodes,
            },
          );
          toast.success("Solicitação para acréscimo de exame atualizada!");
          setNewRequestId(response.data.data.id);
          return response.data.data.id;
        } else {
          const response = await newApi.post<{ data: any }>(
            "/addition",
            {
              appointment_id: appointmentId,
              files: mappedFiles,
              exam_code: examCodes,
            },
          );
          toast.success("Solicitação para acréscimo de exame realizada!");
          setNewRequestId(response.data.data.id);
          setFiles(undefined);
          setSelectedExams(undefined);
          return response.data.data.id;
        }
      } catch (error: any) {
        console.error(error);
        toast.error(
          error?.response?.data?.message ??
          "Ocorreu um erro ao processar a solicitação. Tente novamente.",
        );
      } finally {
        setIsLoading(false);
      }
    },

    async createCompulsory(
      params: ICreateRequestCompulsoryParams,
    ): Promise<ICreateRequestCompulsoryResponse["data"] | undefined> {
      try {
        if (!params.appointment_id) {
          toast.error("ID do atendimento é obrigatório");
          return;
        }

        let fileUrl = "";

        if (params.file && params.file instanceof File) {
          const uploadedFile = await uploadFiles(params.file);
          fileUrl = uploadedFile.path;
        } else if (typeof params.file === 'string') {
          fileUrl = params.file;
        }

        const response = await newApi.post<ICreateRequestCompulsoryResponse>(
          "/compulsory",
          {
            appointment_id: params.appointment_id,
            sinan: params.sinan,
            file_url: fileUrl,
          },
        );

        if (response.data.message.includes("já existe")) {
          toast.info(response.data.message);
        } else {
          toast.success(response.data.message);
        }

        return response.data.data;
      } catch (error: any) {
        const errorMessage = error?.response?.data?.error;

        if (error?.response?.status === 404) {
          toast.error("Requisição de adição de exame não encontrada");
        } else if (error?.response?.status === 409) {
          toast.info("Requisição compulsória já existe para este atendimento");
        } else {
          toast.error(
            errorMessage ??
            "Erro ao criar requisição compulsória. Tente novamente.",
          );
        }

        console.error("CreateCompulsory error:", error);
      }
    },

    async getCompulsory(
      appointmentId: number,
    ): Promise<ICreateRequestCompulsoryResponse["data"] | undefined> {
      if (!appointmentId) return;

      const response = await newApi.get(
        `/compulsory/${appointmentId}`,
      );

      return response.data;
    },

    async updateCompulsoryStatus(
      compulsoryId: number,
      newStatus: number,
      data?: {
        withdrawal_name: string;
        withdrawal_rg: string;
      },
    ): Promise<ICreateRequestCompulsoryResponse["data"] | undefined> {
      try {
        if (!compulsoryId) {
          toast.error("ID da requisição compulsória é obrigatório");
          return;
        }

        const response = await newApi.put<ICreateRequestCompulsoryResponse>(
          `/compulsory/${compulsoryId}`,
          {
            status: newStatus,
            ...data,
          },
        );

        toast.success("Status atualizado com sucesso!");
        return response.data.data;
      } catch (error: any) {
        const errorMessage = error?.response?.data?.error;

        if (error?.response?.status === 404) {
          toast.error("Requisição compulsória não encontrada");
        } else {
          toast.error(
            errorMessage ??
            "Erro ao atualizar requisição compulsória. Tente novamente.",
          );
        }

        console.error("UpdateCompulsoryStatus error:", error);
      }
    },

    async deleteCompulsory(appointment_id: number): Promise<boolean> {
      try {
        if (!appointment_id) {
          toast.error("ID do atendimento é obrigatório");
          return false;
        }

        await newApi.delete(`/compulsory/${appointment_id}`);

        toast.success("Requisição compulsória removida com sucesso");
        return true;
      } catch (error: any) {
        const errorMessage = error?.response?.data?.error;

        if (error?.response?.status === 404) {
          toast.error("Requisição compulsória não encontrada");
        } else {
          toast.error(
            errorMessage ??
            "Erro ao remover requisição compulsória. Tente novamente.",
          );
        }

        console.error("DeleteCompulsory error:", error);
        return false;
      }
    },

    async handleUpdateRequest(
      requestId: number,
      data: {
        exams: Array<{
          code: number;
          name: string;
          isRemoved?: boolean;
          status?: number;
        }>;
        examsAdded: Array<{ code: number; name: string }>;
        examsRemoved: Array<{ code: number; name: string }>;
        files: any[];
      },
    ): Promise<void> {
      try {
        setIsLoading(true);

        const newFiles = data.files.filter(
          (file: any) => !file.id && file instanceof File,
        );

        const uploadedNewFiles: any[] = [];

        if (newFiles.length > 0) {
          const uploadPromises = newFiles.map(async (file) => {
            const uploadSuccess = await uploadFiles(file);
            if (!uploadSuccess || !uploadSuccess.path) {
              toast.error("Erro ao fazer upload de arquivo, tente novamente");
              throw new Error("Arquivo com erro no upload");
            }
            return uploadSuccess.path;
          });

          const uploadedPaths = await Promise.all(uploadPromises);
          uploadedNewFiles.push(...uploadedPaths);
        }

        const examsRemovedPrepared = data.exams
          .filter((exam) => exam.isRemoved)
          .map((exam) => ({
            code: exam.code,
            name: exam.name,
            status: exam.status,
          }));

        const response = await newApi.put(`/addition/${requestId}`, {
          exams: data.exams,
          examsAdded: data.examsAdded,
          examsRemoved: examsRemovedPrepared,
          files: uploadedNewFiles,
        });

        toast.success("Solicitação atualizada com sucesso!");
        return response.data;
      } catch (error: any) {
        console.error(error);
        toast.error(
          error?.response?.data?.message ?? "Erro ao atualizar solicitação",
        );
        throw error;
      } finally {
        setIsLoading(false);
      }
    },

    async getRequestCounters(
      filters: IRequestAdditionFilterParams,
    ): Promise<RequestCounters> {
      const response = await newApi.get("/requests/counters", { params: filters });
      return response.data;
    },

    async getRequestDetail(addition_id: number) {
      try {
        const response = newApi.get<IGetRequestDetailResponse>(
          `/exam/addition/${addition_id}`,
        );
        return (await response).data;
      } catch (error: any) {
        toast.error(error?.response?.data?.message);
      }
    },

    async updateRequestStatus(
      requestId: number,
      newStatus: number,
    ): Promise<void> {
      try {
        await newApi.put(`/addition/${requestId}`, {
          status: newStatus,
        });
        toast.success("Status atualizado com sucesso!");
      } catch (error: any) {
        const errorMessage = error?.response?.data?.error;
        toast.error(
          errorMessage ?? "Erro ao atualizar status. Tente novamente.",
        );
        throw error;
      }
    },

    statusOptions: Object.keys(REQUEST_ADDITION_STATUS_CONTENT).map((key) => ({
      id: Number(key),
      name: REQUEST_ADDITION_STATUS_CONTENT[
        Number(key) as keyof typeof REQUEST_ADDITION_STATUS_CONTENT
      ],
    })),
  };
}
